<template>
  <page-layout
    class="user-list"
    hide-top-bar
    :loading="loading"
  >
    <scroll-container>
      <m-dialog
        v-model:value="showImportModal"
        :title="$t('userList.importModal.header')"
        hide-footer
        no-padding
      >
        <m-content
          padding
          class="_csv-import-content"
        >
          <i18n-t
            keypath="userList.importModal.text"
            class="_terms"
            tag="div"
          >
            <template #link>
              <m-link
                :href="$t('userList.importModal.link')"
                underline-on-hover
              >{{ $t('userList.importModal.here') }}
              </m-link>
            </template>
          </i18n-t>
          <input
            ref="fileinput"
            type="file"
            :style="{display: 'none'}"
            @change="importFileChange"
          >
          <div class="_reactivate-toggle">
            <m-tooltip>
              <m-switch
                v-model:value="ignoreDeactivated"
                :label="$t('userList.importModal.ignoreDeactivated.label')"
              />
              <template #title>
                {{ $t('userList.importModal.ignoreDeactivated.tooltip') }}
              </template>
            </m-tooltip>
          </div>
          <div class="_select-button">
            <m-btn
              icon="upload"
              @click="selectCSV"
            >
              {{ $t('userList.importModal.fileSelect') }}
            </m-btn>
            <div
              v-if="csvFile !== null"
              :class="['_selected-file', importValidationWarnings.length > 0 ? '-warning' : null, !csvValid ? '-error' : null]"
            >
              <m-icon
                class="_icon"
                type="paper-clip"
              />
              <div>{{ csvFile.name }}</div>
            </div>
          </div>
          <template v-if="csvFile !== null">
            <div class="_errors-container">
              <div v-if="importValidationWarnings.length > 0">
                <div>{{ $t('userList.importModal.warnings') }}</div>
                <ul class="_warnings">
                  <li
                    v-for="err in importValidationWarnings"
                    :key="`${err.failure}.${err.value}`"
                  >
                    <span v-if="err.row !== undefined">[{{ $t('userList.importModal.row', { row: err.row }) }}] </span>
                    {{ $t(`importError.${err.failure}`) }}<span v-if="err.value !== ''">: {{ err.value }}</span>
                  </li>
                </ul>
              </div>
              <div v-if="!csvValid">
                <div>{{ $t('userList.importModal.errors') }}</div>
                <ul class="_errors">
                  <li v-if="importValidationErrors.length === 0">
                    {{ $t(`importError.unknown`) }}
                  </li>
                  <li
                    v-for="err in importValidationErrors"
                    :key="`${err.failure}.${err.value}`"
                  >
                    <span v-if="err.row !== undefined">[{{ $t('userList.importModal.row', { row: err.row }) }}] </span>
                    {{ $t(`importError.${err.failure}`) }}: {{ err.value !== '' ? err.value : $t('userList.importModal.emptyValue') }}
                  </li>
                </ul>
              </div>
            </div>
          </template>
        </m-content>
        <m-default-form-actions
          :clickable="csvValid"
          :loading="csvImportLoading"
          :submit-text="$t('userList.importModal.submit')"
          @cancel="resetImportModal"
          @submit="importCSV"
        />
      </m-dialog>
      <m-content
        padding-x="layout"
        class="_header"
      >
        <page-header
          :title="$t('userList.title')"
          heading="h3"
          underlined
          boxed
          no-padding-x
        />
      </m-content>
      <m-content
        padding-x="layout"
        boxed
        class="_table-header"
      >
        <m-alert
          v-if="showDeleteDemoUserBox"
          class="_alert-box"
          type="warning"
          hide-icon
        >
          <div class="_text">
            {{ $t("userList.removeDemoUsers.text") }}
          </div>
          <m-btn
            color="secondary"
            @click="deleteDemoUsers"
          >
            {{ $t('userList.removeDemoUsers.button') }}
          </m-btn>
        </m-alert>
        <m-alert
          v-if="notActiveUsers.length > 1"
          class="_bulk-invitation-box _alert-box"
          type="warning"
          hide-icon
        >
          <i18n-t
            tag="div"
            class="_text"
            keypath="userList.bulkInvitationBox.text"
          >
            <template #users>
              <span class="_text-bold">
                {{ $t('userList.bulkInvitationBox.userCount', { userCount: notActiveUsers.length }) }}
              </span>
            </template>
          </i18n-t>
          <m-btn
            color="secondary"
            @click="confirmBulkInviteUsers(notActiveUsers)"
          >
            {{ $t('userList.bulkInvitationBox.bulkAction', { userCount: notActiveUsers.length }) }}
          </m-btn>
        </m-alert>
        <list-header class="_list-header">
          <template
            #left
          >
            <list-tab
              v-for="filter in filterList"
              :key="filter.value"
              :active="selectedFilter === filter.value"
              @click="selectedFilter = filter.value"
            >
              <item-title>
                <template #title>
                  {{ filter.text }}
                  <div
                    :style="{ color: $colors.grey.base, backgroundColor: 'transparent', paddingLeft: '.4rem' }"
                  >
                    {{ filter.count }}
                  </div>
                </template>
              </item-title>
            </list-tab>
          </template>
          <template
            v-if="canCreate"
            #right
          >
            <list-string-filter
              class="_btn"
              @input="val => search = val"
            />
            <div class="_btn">
              <m-tooltip :disabled="!userLimitReached">
                <template #title>
                  {{ $t('userList.trialUserLimitReached', { limit: userLimitValue }) }}
                </template>
                <m-btn
                  hide-border
                  light
                  :disabled="userLimitReached"
                  @click="showImportModal = true"
                >
                  {{ $t('userList.importModalButton') }}
                </m-btn>
              </m-tooltip>
            </div>
            <div class="_btn">
              <m-btn
                hide-border
                light
                :href="`/api/v1/csv-user-export?lang=${userLang}&filter=${selectedFilter}`"
              >
                {{ $t('userList.exportUsers') }}
              </m-btn>
            </div>
            <m-tooltip :disabled="!userLimitReached">
              <m-btn
                color="primary"
                :disabled="userLimitReached"
                @click="showAddModal = true"
              >
                {{ $t('userList.add') }}
              </m-btn>
              <template #title>
                {{ $t('userList.trialUserLimitReached', { limit: userLimitValue }) }}
              </template>
            </m-tooltip>
            <m-dialog
              v-model:value="showAddModal"
              :title="$t('userList.add')"
              center
              hide-footer
              no-padding
              fade-in
            >
              <invite-users
                :key="showAddModal"
                can-skip-invite-email
                @close="showAddModal = false"
              />
            </m-dialog>
          </template>
        </list-header>
      </m-content>
      <m-content
        boxed
        padding-x="layout"
        class="_table"
      >
        <user-list-table
          :entities="[...users, ...archivedUsers]"
          :account="account"
          :search="search"
          :filter="selectedFilter"
        />
      </m-content>
    </scroll-container>
  </page-layout>
</template>

<script>
import InviteUsers from '@/components/onboarding/InviteUsers.vue';
import ItemTitle from '@/components/ItemTitle.vue';
import ListHeader from '@/components/list/ListHeader.vue';
import ListStringFilter from '@/components/list/ListStringFilter.vue';
import ListTab from '@/components/list/ListTab.vue';
import PageHeader from 'shared/components/PageHeader.vue';
import PageLayout from '@/components/page/PageLayout.vue';
import ScrollContainer from '@/components/page/ScrollContainer.vue';
import UserListTable from '@/components/UserListTable.vue';
import useAccess from '@/composables/access/access';
import useInvitations from '@/composables/user/invitations/invitations';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useUserLimit from '@/composables/user/user-limit';
import useUsers from '@/composables/user/users';
import { USER_STATUS_NO_INVITATION } from '@/lib/constants';
import { accessGroupFlag } from 'shared/constants.json';
import { computed } from 'vue';
import { doImportCSV, doResetAccount, doValidateCSV } from '@/api';
import { experimentFlag } from 'shared/experiments.json';
import { importValidationFailureType, userListFilter } from '@/constants/bumblebee.json';
import { useI18n } from 'vue-i18n';

export default {
  name: 'UserList',
  components: {
    ListStringFilter,
    ScrollContainer,
    PageLayout,
    InviteUsers,
    PageHeader,
    UserListTable,
    ListTab,
    ItemTitle,
    ListHeader,
  },
  setup() {
    const { userHasRight, accountHasFeature } = useAccess();
    const { loggedInUser, userLang } = useLoggedInUser();
    const { loggedInUserAccount } = useLoggedInUserAccount();
    const { getActivationStatus, users, archivedUsers } = useUsers();
    const { inviteUsers } = useInvitations();
    const { userLimitReached, userLimitValue } = useUserLimit();
    const { t } = useI18n();

    const filterList = computed(() => {
      const counts = {
        [userListFilter.active]: users.value.length,
        [userListFilter.archived]: archivedUsers.value.length,
        [userListFilter.none]: [...users.value, ...archivedUsers.value].length,
      };

      return userListFilter.all.map((f) => ({
        text: t(`userList.filters.${f}`),
        value: f,
        count: counts[f],
      }));
    });

    return {
      userHasRight,
      user: loggedInUser,
      userLang,
      account: loggedInUserAccount,
      users,
      archivedUsers,
      getActivationStatus,
      inviteUsers,
      accountHasFeature,
      filterList,
      userLimitReached,
      userLimitValue,
    };
  },
  data() {
    return {
      showAddModal: false,
      showImportModal: false,
      csvFile: null,
      csvImportLoading: false,
      importValidationResponse: null,
      loading: false,
      search: '',
      selectedFilter: this.filterList[0].value,
      experimentFlag,
      ignoreDeactivated: true,
    };
  },
  computed: {
    importValidationWarnings() {
      return this.importValidationResponse?.errors?.filter((e) => e.type === importValidationFailureType.warning) ?? [];
    },
    importValidationErrors() {
      return this.importValidationResponse?.errors?.filter((e) => e.type === importValidationFailureType.error) ?? [];
    },
    csvValid() {
      if (this.importValidationResponse === null) {
        return true;
      }
      if (this.importValidationResponse.errors === undefined) {
        return false;
      }
      return this.importValidationErrors.length === 0;
    },
    showDeleteDemoUserBox() {
      return this.userHasRight([accessGroupFlag.accountWriteAccess]) && this.users.filter((u) => u.isDemo).length > 0;
    },
    notActiveUsers() {
      return this.users.filter(this.needsInvitation);
    },
    canCreate() {
      return this.userHasRight([accessGroupFlag.userWriteAccess]);
    },
  },
  methods: {
    selectCSV() {
      this.$refs.fileinput.click();
    },
    importFileChange(event) {
      if (event.target.files.length === 0) {
        return;
      }

      doValidateCSV(event.target.files[0], this.userLang, this.ignoreDeactivated).then((resp) => {
        this.csvFile = event.target.files[0];
        this.$refs.fileinput.value = '';

        this.importValidationResponse = resp.data;
      });
    },
    resetImportModal() {
      this.showImportModal = false;
      this.ignoreDeactivated = true;
    },
    importCSV() {
      this.csvImportLoading = true;
      doImportCSV(this.csvFile, this.userLang, this.ignoreDeactivated).then((resp) => {
        if (resp.status !== 200) {
          this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
          return;
        }
        this.resetImportModal();
      }).finally(() => {
        this.csvFile = null;
        this.csvImportLoading = false;
      });
    },
    confirmBulkInviteUsers(users) {
      const bulkInviteMethod = () => this.inviteUsers(users).then(() => {
        this.$showSnackbar({ color: 'success', message: this.$t('inviteUsers.success') });
      }).catch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      });

      this.$confirm({
        title: this.$t('userList.bulkInvitationDialog.text', { userCount: users.length }),
        okText: this.$t('userList.bulkInvitationDialog.confirmAction', { userCount: users.length }),
        okType: 'primary',
        maskClosable: true,
        cancelText: this.$t('general.cancel'),
        onOk() {
          bulkInviteMethod();
        },
      });
    },
    needsInvitation(user) {
      return this.getActivationStatus(user) === USER_STATUS_NO_INVITATION;
    },
    deleteDemoUsers() {
      const resetMethod = () => doResetAccount({ demoUsers: true }).then((response) => {
        this.loading = false;
        if (response.status === 401) {
          this.$showSnackbar({ color: 'error', message: this.$t('error.unauthorizedAction') });
          return;
        }
        if (response.status !== 200) {
          this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
          return;
        }
        this.$showSnackbar({ color: 'success', message: this.$t('success.deleted') });
        window.location.reload();
      });
      this.$confirm({
        title: this.$t('userList.removeDemoUsers.confirmText'),
        okText: this.$t('userList.removeDemoUsers.confirmAction'),
        okType: 'primary',
        maskClosable: true,
        cancelText: this.$t('general.cancel'),
        onOk() {
          resetMethod();
        },
      });
    },
  },
};
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  .user-list {
    ._alert-box {
      display: flex;
      align-items: center;
      margin-bottom: 3rem;

      ._text {
        padding-right: 2.4rem;
        margin-right: auto;
        white-space: pre-wrap;
      }

      ._text-bold {
        font-weight: $font-weight-semibold;
      }
    }

    ._header {
      position: sticky;
      left: 0;
      margin-bottom: 4rem;
    }

    ._table-header {
      position: sticky;
      left: 0;
      display: flex;
      align-items: center;
      padding-bottom: .4rem;

      :deep(._list-header) {
        flex: 1 1 auto;
      }
    }

    ._left {
      display: flex;
      margin-right: 2rem;
      font-size: $font-size-5;
      font-weight: $font-weight-bold;
    }

    ._right {
      display: flex;
      flex-direction: row;
      margin-left: auto;
      overflow: auto;

      ._btn {
        margin-right: .2rem;
      }
    }
  }

  ._csv-import-content {
    ._terms {
      margin-bottom: .8rem;
    }

    ._reactivate-toggle {
      margin-bottom: .8rem;
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }

    ._select-button {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
    }

    ._selected-file {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: flex;
      flex-direction: row;
      align-items: center;

      &.-warning {
        color: $warning-color;
      }

      &.-error {
        color: $error-color;
      }

      ._icon {
        margin-right: .8rem;
      }
    }

    ._errors-container {
      margin-top: 0.8rem;
      max-height: 50rem;
      overflow-y: auto;

      ._warnings {
        color: $warning-color;
      }

      ._errors {
        color: $error-color;
      }

      ul {
        margin-bottom: 0;
      }
    }
  }
</style>
